
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    /*
     * Avoid stacking multiple focus handlers
     * if the drawer is already opening or open.
     */

    if (this.classList.contains('active')) {
      return;
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 0);

    this.addEventListener(
      'transitionend',
      (event) => {
        /*
         * Ignore transition events from
         * nested cart elements.
         */

        if (event.target !== this) return;

        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : this.querySelector('#CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        /*
         * Never call trapFocus with null.
         */

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget
          .closest('details')
          .hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    /*
     * Validate Shopify's response before
     * replacing any cart HTML.
     */

    if (!parsedState || !parsedState.sections) {
      console.error(
        'CartDrawer: Shopify response has no sections.',
        parsedState
      );
      return;
    }

    const sections = this.getSectionsToRender();

    /*
     * Validate ALL required HTML before
     * changing the visible cart.
     */

    const replacements = [];

    for (const section of sections) {
      const html = parsedState.sections[section.id];

      if (typeof html !== 'string' || !html.trim()) {
        console.error(
          'CartDrawer: Missing section:',
          section.id
        );
        return;
      }

      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!target) {
        console.error(
          'CartDrawer: Target not found:',
          section.id
        );
        return;
      }

      const source = this.getSectionDOM(
        html,
        section.selector || '.shopify-section'
      );

      if (!source) {
        console.error(
          'CartDrawer: Invalid section HTML:',
          section.id
        );
        return;
      }

      replacements.push({
        id: section.id,
        target: target,
        html: source.innerHTML
      });
    }

    /*
     * Apply the validated Shopify HTML.
     */

    replacements.forEach((replacement) => {
      replacement.target.innerHTML = replacement.html;
    });

    /*
     * Synchronise the empty-cart state
     * after the new HTML has been inserted.
     */

    const cartItems = this.querySelector(
      '#CartDrawer .cart-item'
    );

    if (cartItems) {
      this.classList.remove('is-empty');

      const inner = this.querySelector('.drawer__inner');

      if (inner) {
        inner.classList.remove('is-empty');
      }
    }

    this.productId = parsedState.id;

    /*
     * Custom theme feature:
     * update sticky header cart counter.
     *
     * The sticky icon may not exist on
     * every page, especially PageFly.
     */

    const cartIcon = document.getElementById(
      'cart-icon-bubble'
    );

    const stickyIcon = document.querySelector(
      '.header__icon--cart_sticky'
    );

    if (cartIcon && stickyIcon) {
      const countEl = cartIcon.querySelector(
        "span[aria-hidden='true']"
      );

      const countTextEl = cartIcon.querySelector(
        '.visually-hidden'
      );

      const stickyCountEl = stickyIcon.querySelector(
        "span[aria-hidden='true']"
      );

      const stickyCountTextEl = stickyIcon.querySelector(
        '.visually-hidden'
      );

      if (countEl && stickyCountEl) {
        stickyCountEl.innerText = countEl.innerText;
      }

      if (countTextEl && stickyCountTextEl) {
        stickyCountTextEl.innerText =
          countTextEl.innerText;
      }
    }

    /*
     * Preserve your existing PageFly
     * cart-updated event.
     */

    try {
      if (typeof publish === 'function') {
        publish('cart-updated', {
          cartData: parsedState
        });
      }
    } catch (error) {
      console.error(
        'CartDrawer: publish error:',
        error
      );
    }

    /*
     * Open the drawer after updating
     * its contents.
     */

    setTimeout(() => {
      const overlay = this.querySelector(
        '#CartDrawer-Overlay'
      );

      if (overlay) {
        overlay.onclick = () => this.close();
      }

      this.open();

      console.log(
        'CartDrawer: updated and opened.',
        {
          productRows:
            this.querySelectorAll(
              '#CartDrawer .cart-item'
            ).length,
          drawerExists:
            !!this.querySelector('#CartDrawer')
        }
      );
    }, 0);
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const element = this.getSectionDOM(
      html,
      selector
    );

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (typeof html !== 'string') {
      return null;
    }

    const documentFragment = new DOMParser()
      .parseFromString(html, 'text/html');

    return documentFragment.querySelector(
      selector
    );
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}
